import { NumberFormatCustom } from 'components/index';
import { floatSearch } from 'components/tables/TableAndModal';

export const IGNORED_MESSAGE = 'Ignored, no changes detected.';
export const SUCCESS_MESSAGE = 'Success.';
export const RATE_DELETED = 'Rate has been deleted.';
export const CSV_TYPE = 'text/csv';
export const CSV_TYPE_FX = 'application/vnd.ms-excel';
export const CSV_TYPE_ERROR = 'File must be .csv type';
export const NO_DATA_FOUND_ERROR = 'We didn\'t detect any changes.';
export const RATE_NOT_ACTIVE = 'This rate is not active.';
export const RATE_DNE_ERROR = 'This rate ID does not exist in your company.';
export const PREFIX_ERROR = 'This prefix is invalid.';
export const INVALID_PRIORITY_ERROR = 'This priority is invalid.';
export const INVALID_RATE_NAME = 'This rate name is invalid.';
export const INVALID_RATE_ERROR = 'This rate has invalid numbers.';
export const INVALID_SIZE = 'This block size is invalid.';
export const INVALID_PATTERN = 'This pattern/prefix is invalid.';

export const FIELDS_CHANGE_NEED_VALIDATION = [
    // 'id',
    'name',
    'priority',
    'prefix',
];
export const FIELDS_CHANGE_NO_NEED_VALIDATION = [
    'flagFallInRate',
    'flagFallOutRate',
    'inboundRate',
    'outboundRate',
    'isActive',
    'id',
];

/** NOTE: enum for csv template */
export const REQUIRED_FIELDS_CR = {
    id: 'Rate ID',
    name: 'Rate Name',
    priority: 'Priority',
    prefix: 'Prefix',
};

export const RATES_FIELDS_CR = {
    flagFallInRate: 'Flag Fall In Rate',
    flagFallOutRate: 'Flag Fall Out Rate',
    inboundRate: 'Inbound Rate',
    outboundRate: 'Outbound Rate',
};

/** Calling Rates Fields */
const CALLING_RATES_FIELDS = {
    ...REQUIRED_FIELDS_CR,
    ...RATES_FIELDS_CR,
    isActive: 'Active', // isActive: true -> Active: 1, isActive: false -> Active: 0
};

export const REQUIRED_FIELDS_NBR = {
    id: 'NB Rate ID',
    size: 'Block Size',
    prefix: 'Prefix',
    pattern: 'Pattern',
};

export const RATES_FIELDS_NBR = {
    setupFee: 'Setup Fee',
    rate: 'Rate',
};

/** Number Block Rates Fields */
export const NUMBERBLOCK_RATES_FIELDS = {
    ...REQUIRED_FIELDS_NBR,
    ...RATES_FIELDS_NBR,
    bill: 'Bill',
    isActive: 'Active', // isActive: true -> Active: 1, isActive: false -> Active: 0
};

export const TEMPLATE_FILENAME_CR = 'CallingRatesTemplate.csv';
export const TEMPLATE_FILENAME_NBR = 'NumberBlockRatesTemplate.csv';

export const CONFIG = {
    callingRates: {
        fields: CALLING_RATES_FIELDS,
        requiredFields: Object.keys(REQUIRED_FIELDS_CR),
        rateFields: Object.keys(RATES_FIELDS_CR),
        fileName: 'CallingRates',
        templateFilename: 'CallingRatesTemplate.csv',
        endpoint: 'rates',
    },
    numberBlockRates: {
        fields: NUMBERBLOCK_RATES_FIELDS,
        requiredFields: Object.keys(REQUIRED_FIELDS_NBR),
        rateFields: Object.keys(RATES_FIELDS_NBR),
        fileName: 'NumberBlockRates',
        templateFilename: 'NumberBlockRatesTemplate.csv',
        endpoint: 'numberblockrates',
    },
};

export const sharedColumns = [
    {
        title: 'Name',
        field: 'name',
    },
    {
        title: 'Priority',
        field: 'priority',
    },
    {
        title: 'Prefix',
        field: 'prefix',
    },
    {
        title: 'Flag Fall In Rate',
        field: 'flagFallInRate',
        donotOverrideSort: true,
        render: (rowData) => {
            return (
                <NumberFormatCustom
                    displayType="text"
                    value={rowData['flagFallInRate']}
                    allowNegative
                />
            );
        },
        customFilterAndSearch: (v, rowData) =>
            floatSearch(v, rowData, 'flagFallInRate'),
    },
    {
        title: 'Flag Fall Out Rate',
        field: 'flagFallOutRate',
        doNotOverrideSort: true,
        render: (rowData) => {
            return (
                <NumberFormatCustom
                    displayType="text"
                    value={rowData['flagFallOutRate']}
                    allowNegative
                />
            );
        },
        customFilterAndSearch: (v, rowData) =>
            floatSearch(v, rowData, 'flagFallOutRate'),
    },
    {
        title: 'Inbound Rate',
        field: 'inboundRate',
        doNotOverrideSort: true,
        render: (rowData) => {
            return (
                <NumberFormatCustom
                    displayType="text"
                    value={rowData['inboundRate']}
                    allowNegative
                />
            );
        },
        customFilterAndSearch: (v, rowData) =>
            floatSearch(v, rowData, 'inboundRate'),
    },
    {
        title: 'Outbound Rate',
        field: 'outboundRate',
        doNotOverrideSort: true,
        render: (rowData) => {
            return (
                <NumberFormatCustom
                    displayType="text"
                    value={rowData['outboundRate']}
                    allowNegative
                />
            );
        },
        customFilterAndSearch: (v, rowData) =>
            floatSearch(v, rowData, 'outboundRate'),
    },
];

export const passedColumns = [
    ...sharedColumns,
    {
        title: 'Verification Status',
        field: 'verificationStatus',
    },
];

export const failedColumns = [
    ...sharedColumns,
    {
        title: 'Failure Reasons',
        field: 'errors',
        render: (rowData) => {
            if (Array.isArray(rowData.errors) && rowData.errors.length > 0) {
                const errorString = rowData.errors.join('\n');
                return (
                    <span style={{ whiteSpace: 'pre-line' }}>
                        {errorString}
                    </span>
                );
            }
            return null;
        },
    },
].map((v) => ({
    ...v,
    defaultSort: 'asc',
}));

const sharedNbrColumns = [
    {
        title: 'Block Size',
        field: 'size',
    },
    {
        title: 'Bill',
        field: 'bill',
        type: 'boolean',
    },
    {
        title: 'Setup Fee',
        field: 'setupFee',
        doNotOverrideSort: true,
        render: (rowData) => {
            return (
                <NumberFormatCustom
                    displayType="text"
                    value={rowData['setupFee']}
                    allowNegative
                />
            );
        },
        customFilterAndSearch: (v, rowData) =>
            floatSearch(v, rowData, 'setupFee'),
    },
    {
        title: 'Rate',
        field: 'rate',
        doNotOverrideSort: true,
        render: (rowData) => {
            return (
                <NumberFormatCustom
                    displayType="text"
                    value={rowData['rate']}
                    allowNegative
                />
            );
        },
        customFilterAndSearch: (v, rowData) => floatSearch(v, rowData, 'rate'),
    },
    {
        title: 'Prefix',
        field: 'prefix',
    },
    {
        title: 'Pattern',
        field: 'pattern',
    },
];

export const passedNbrColumns = [
    ...sharedNbrColumns,
    {
        title: 'Verification Status',
        field: 'verificationStatus',
    },
];

export const failedNbrColumns = [
    ...sharedNbrColumns,
    {
        title: 'Failure Reasons',
        field: 'errors',
        render: (rowData) => {
            if (Array.isArray(rowData.errors) && rowData.errors.length > 0) {
                const errorString = rowData.errors.join('\n');
                return (
                    <span style={{ whiteSpace: 'pre-line' }}>
                        {errorString}
                    </span>
                );
            }
            return null;
        },
    },
].map((v) => ({
    ...v,
    defaultSort: 'asc',
}));

export const succeedAPIColumns = [
    {
        title: 'Name',
        field: 'name',
    },
];

export const failedAPIColumns = [
    {
        title: 'Name',
        field: 'name',
    },
    {
        title: 'Failure Reasons',
        field: 'apiErrors',
    },
];

export const succeedNbrAPIColumns = [
    {
        title: 'Block Size',
        field: 'size',
    },
    {
        title: 'Prefix',
        field: 'prefix',
    },
    {
        title: 'Pattern',
        field: 'pattern',
    },
    {
        title: 'Setup Fee',
        field: 'setupFee',
    },
    {
        title: 'Rate',
        field: 'rate',
    },
];

export const failedNbrAPIColumns = [
    {
        title: 'Block Size',
        field: 'size',
    },
    {
        title: 'Prefix',
        field: 'prefix',
    },
    {
        title: 'Pattern',
        field: 'pattern',
    },
    {
        title: 'Setup Fee',
        field: 'setupFee',
    },
    {
        title: 'Rate',
        field: 'rate',
    },
    {
        title: 'Failure Reasons',
        field: 'apiErrors',
    },
];

export const DEFAULT_VERIFICATION_RESULT = {
    passed: {
        columns: [],
        data: [],
    },
    failed: {
        columns: [],
        data: [],
    },
};

export let VERIFICATION_CHECKS = [
    {
        label: 'Received CSV',
        check: false,
        current: true,
        completionCounter: 0,
        total: 1,
    },
    {
        label: 'Fetching Number Block Rates',
        check: false,
        current: false,
        completionCounter: 0,
        total: 4,
    },
    {
        label: 'Converting CSV',
        check: false,
        current: false,
        completionCounter: 0,
        total: 3,
    },
    {
        label: 'Verifying Number Block Rate ID',
        check: false,
        current: false,
        completionCounter: 0,
        total: 5,
    },
    {
        label: 'Verifying Deleted Rate',
        check: false,
        current: false,
        completionCounter: 0,
        total: 4,
    },
];

export const DEFAULT_JSON_DATA = {
    errors: [],
    ignored: false,
    isNewRate: false,
};

export const API_RESULT_FIELDS_TO_EXPORT = {
    ...CONFIG.numberBlockRates.fields,
    apiErrors: 'Errors',
};

export const INVALID_DATA_FILENAME = 'InvalidData.csv';
